import { create } from 'zustand';
import { http } from '../lib/api';
import { useUiStore } from './ui';

export interface PlatformBranding {
  platformName: string;
  logoUrl: string | null;
  brandColor: string | null;
  defaultPlayerTheme: string;
  faviconUrl: string | null;
  loginTitle: string | null;
  loginTagline: string | null;
  footerText: string | null;
  supportEmail: string | null;
  address: string | null;
  supportHours: string | null;
  anotherPhone: string | null;
  /** Admin Settings → Messaging "WhatsApp Sender Number" (wa.me support link). */
  supportWhatsapp: string | null;
}

const DEFAULT_BRANDING: PlatformBranding = {
  platformName: 'RTMPHUB',
  logoUrl: null,
  brandColor: null,
  defaultPlayerTheme: 'dark',
  faviconUrl: null,
  loginTitle: null,
  loginTagline: null,
  footerText: null,
  supportEmail: null,
  address: null,
  supportHours: null,
  anotherPhone: null,
  supportWhatsapp: null,
};

interface BrandingState extends PlatformBranding {
  loaded: boolean;
  load: () => Promise<void>;
}

/**
 * Global platform-wide white-label branding, configured by platform admins in
 * the admin panel (Admin → Settings). Public `GET /api/platform/branding`.
 */
export const useBrandingStore = create<BrandingState>((set) => ({
  ...DEFAULT_BRANDING,
  loaded: false,
  load: async () => {
    try {
      const b = await http.get<PlatformBranding>('/api/platform/branding');
      const next = { ...DEFAULT_BRANDING, ...b, loaded: true };
      set(next);
      document.title = next.platformName;
      // Swap the browser-tab icon when a custom favicon is configured.
      const faviconHref = next.faviconUrl?.trim();
      if (faviconHref) {
        let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = faviconHref;
      }
      useUiStore.getState().applyBrandColor(next.brandColor ?? undefined);
    } catch {
      set({ loaded: true });
    }
  },
}));
